import React, { useState, useEffect, useCallback } from 'react';
import { carouselApiService } from '../services/carouselApiService';
import { Slide } from '../types';
import SlideCard from './SlideCard';
import CompactSlideCard from './CompactSlideCard';

/**
 * The CLIENT/VENDOR Slides-tab browse surface (AI-Curated Feed Phase 3b, Workstream CE).
 * A plain, recency-ordered, paginated list of the slides currently cube-shared with the caller
 * for the selected shop — no filters, no categories, no text search (see the proposal's D-filters
 * decision; query/exploration lives in Carousel search). The standard/compact card-size toggle is
 * kept (a display preference, not a filter). Backed entirely by carousel-svc's
 * GET /api/v1/carousel/slides (BigQuery + the visibility TVF, role-stripped); external members never
 * hit slide-svc for slide data. Slides render read-only: SlideCard/CompactSlideCard gate their
 * edit/price/favorite/status controls to internal roles, so external members see images + name only.
 */
interface ClientSlidesBrowseProps {
  shopId: string;
}

const ClientSlidesBrowse: React.FC<ClientSlidesBrowseProps> = ({ shopId }) => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [viewMode, setViewMode] = useState<'standard' | 'compact'>('standard');

  const pageSize = viewMode === 'compact' ? 60 : 20;

  const load = useCallback(async (pageNum: number, size: number) => {
    setLoading(true);
    setError('');
    try {
      const { slides: items, pagination } = await carouselApiService.browseSlides(shopId, pageNum, size);
      setSlides(items);
      setTotalElements(pagination.totalElements);
      setHasNext(pagination.hasNext);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to load shared slides:', err);
      setError('Failed to load slides.');
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  // Load page 0 on mount, when the shop changes, and when the view mode (page size) changes.
  useEffect(() => {
    load(0, viewMode === 'compact' ? 60 : 20);
  }, [viewMode, load]);

  if (loading && slides.length === 0) {
    return (
      <div className="slides-tab-loading">
        <div className="flex items-center justify-center py-12">
          <div className="spinner-sm mr-3"></div>
          <span className="text-muted">Loading slides...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="client-slides-browse">
      {/* View Mode Toggle (display preference — kept for CLIENTs; mirrors the internal Slides tab) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <div style={{ display: 'flex', border: '1px solid var(--border-medium)', borderRadius: '6px', overflow: 'hidden', flex: '0 0 auto' }}>
          <button
            onClick={() => setViewMode('standard')}
            title="Standard view"
            style={{
              padding: '6px 10px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: viewMode === 'standard' ? 'var(--delft-blue)' : 'var(--bg-card)',
              color: viewMode === 'standard' ? 'var(--text-inverse)' : 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.15s ease'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </button>
          <button
            onClick={() => setViewMode('compact')}
            title="Compact view"
            style={{
              padding: '6px 10px',
              border: 'none',
              borderLeft: '1px solid var(--border-medium)',
              cursor: 'pointer',
              backgroundColor: viewMode === 'compact' ? 'var(--delft-blue)' : 'var(--bg-card)',
              color: viewMode === 'compact' ? 'var(--text-inverse)' : 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.15s ease'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="2" width="5" height="5" rx="0.5"/>
              <rect x="9.5" y="2" width="5" height="5" rx="0.5"/>
              <rect x="17" y="2" width="5" height="5" rx="0.5"/>
              <rect x="2" y="9.5" width="5" height="5" rx="0.5"/>
              <rect x="9.5" y="9.5" width="5" height="5" rx="0.5"/>
              <rect x="17" y="9.5" width="5" height="5" rx="0.5"/>
              <rect x="2" y="17" width="5" height="5" rx="0.5"/>
              <rect x="9.5" y="17" width="5" height="5" rx="0.5"/>
              <rect x="17" y="17" width="5" height="5" rx="0.5"/>
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner card mb-6">
          <div className="text-error">{error}</div>
        </div>
      )}

      {slides.length === 0 && !loading ? (
        <div className="empty-state card text-center py-12">
          <h3 className="heading-secondary text-xl mb-2">Nothing shared with you yet</h3>
          <p className="text-muted">Slides a shop shares with you will show up here.</p>
        </div>
      ) : viewMode === 'compact' ? (
        <div className="slides-grid-compact">
          {slides.map(slide => (
            <div key={slide.id}>
              <CompactSlideCard slide={slide} />
            </div>
          ))}
        </div>
      ) : (
        <div className="slides-grid">
          {slides.map(slide => (
            <div key={slide.id}>
              <SlideCard slide={slide} />
            </div>
          ))}
        </div>
      )}

      {/* Pagination Controls (mirrors SlidesTab) */}
      {slides.length > 0 && !loading && (
        <div className="pagination-controls" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          padding: '24px 0',
          borderTop: '1px solid var(--border-light)',
          marginTop: '24px'
        }}>
          <p className="text-muted" style={{ fontSize: '0.875rem', margin: 0 }}>
            Showing slides {page * pageSize + 1}-{Math.min((page + 1) * pageSize, totalElements)} of {totalElements}
          </p>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => {
                if (page > 0) {
                  load(page - 1, pageSize);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              disabled={page === 0 || loading}
              className="btn btn-secondary"
              style={{
                opacity: page === 0 || loading ? 0.5 : 1,
                cursor: page === 0 || loading ? 'not-allowed' : 'pointer'
              }}
            >
              ← Previous Page
            </button>

            <button
              onClick={() => {
                if (hasNext) {
                  load(page + 1, pageSize);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              disabled={!hasNext || loading}
              className="btn btn-secondary"
              style={{
                opacity: !hasNext || loading ? 0.5 : 1,
                cursor: !hasNext || loading ? 'not-allowed' : 'pointer'
              }}
            >
              Next Page →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSlidesBrowse;
